import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
"@/shared/components/ui/table";
import TrailEventModal from '../../components/trails/TrailEventModal';
import {
  ScanLine,
  Search,
  Download,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Filter } from
'lucide-react';

const API_URL = API_BASE;

const ScanTrail = () => {const { t } = useTranslation("wms");
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScan, setSelectedScan] = useState(null);
  const [filters, setFilters] = useState({
    gateId: 'all',
    barcodeValue: '',
    result: 'all',
    dateRange: '24h'
  });

  useEffect(() => {
    fetchScans();
  }, [filters.dateRange]);

  const fetchScans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ezerpToken');
      const params = new URLSearchParams();
      if (filters.gateId) params.append('gate_id', filters.gateId);
      if (filters.barcodeValue) params.append('barcode_value', filters.barcodeValue);
      if (filters.result) params.append('result', filters.result);

      const response = await fetch(`${API_URL}/api/trails/scan/history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setScans(data.scans || []);
      } else {
        setScans(getMockScans());
      }
    } catch (error) {
      console.error('Failed to fetch scans:', error);
      setScans(getMockScans());
    }
    setLoading(false);
  };

  const getMockScans = () => [
  {
    id: 'SCAN-001',
    gate_id: 'GATE_RCV_ITEM',
    barcode_value: '7891234567890',
    barcode_type: 'GTIN',
    scan_result: 'OK',
    operator_id: 'operator@ezerp.com',
    scanned_at: new Date(Date.now() - 60000).toISOString(),
    chain_mapping: { asn_id: 'ASN-001', item_id: 'ITEM-001' }
  },
  {
    id: 'SCAN-002',
    gate_id: 'GATE_PICK_LOC',
    barcode_value: 'A-01-02-03',
    barcode_type: 'LOCATION',
    scan_result: 'OK',
    operator_id: 'operator@ezerp.com',
    scanned_at: new Date(Date.now() - 300000).toISOString(),
    chain_mapping: { order_id: 'ORD-001' }
  },
  {
    id: 'SCAN-003',
    gate_id: 'GATE_PACK_ITEM',
    barcode_value: 'SKU-WRONG',
    barcode_type: 'SKU',
    scan_result: 'REJECTED',
    rejection_reason: 'Item does not belong to this order',
    operator_id: 'operator@ezerp.com',
    scanned_at: new Date(Date.now() - 600000).toISOString(),
    chain_mapping: { order_id: 'ORD-002' }
  },
  {
    id: 'SCAN-004',
    gate_id: 'GATE_LOAD_PKG',
    barcode_value: 'PKG-2026-001',
    barcode_type: 'CARTON',
    scan_result: 'OK',
    operator_id: 'driver@ezerp.com',
    scanned_at: new Date(Date.now() - 900000).toISOString(),
    chain_mapping: { route_id: 'RT-001' }
  },
  {
    id: 'SCAN-005',
    gate_id: 'GATE_STOP_PKG',
    barcode_value: 'PKG-2026-002',
    barcode_type: 'CARTON',
    scan_result: 'WARNING',
    rejection_reason: 'Package scanned at wrong stop',
    operator_id: 'driver@ezerp.com',
    scanned_at: new Date(Date.now() - 1200000).toISOString(),
    chain_mapping: { route_id: 'RT-001', stop_id: 'STOP-002' }
  },
  {
    id: 'SCAN-006',
    gate_id: 'GATE_PUT_BIN',
    barcode_value: 'B-02-03-04',
    barcode_type: 'LOCATION',
    scan_result: 'REJECTED',
    rejection_reason: 'Location does not match assignment',
    operator_id: 'operator@ezerp.com',
    scanned_at: new Date(Date.now() - 1800000).toISOString(),
    chain_mapping: { pallet_id: 'PAL-001' }
  }];


  const getResultBadge = (result) => {
    switch (result) {
      case 'OK':
        return <Badge className="bg-green-500 gap-1"><CheckCircle2 className="h-3 w-3" />{t("ok", "OK")}</Badge>;
      case 'REJECTED':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />{t("rejected", "Rejected")}</Badge>;
      case 'WARNING':
        return <Badge className="bg-amber-500 gap-1"><AlertTriangle className="h-3 w-3" />{t("warning", "Warning")}</Badge>;
      default:
        return <Badge variant="secondary">{result}</Badge>;
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString();
  };

  const handleSearch = () => {
    fetchScans();
  };

  // Stats
  const stats = {
    total: scans.length,
    ok: scans.filter((s) => s.scan_result === 'OK').length,
    rejected: scans.filter((s) => s.scan_result === 'REJECTED').length,
    warning: scans.filter((s) => s.scan_result === 'WARNING').length
  };

  return (
    <div className="p-6 space-y-6" data-testid="scan-trail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
            <ScanLine className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("scanTrail", "Scan Trail")}</h1>
            <p className="text-muted-foreground">{t("barcodeProofChain", "Barcode proof chain - Amazon-style tracking")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchScans}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">{t("totalScans", "Total Scans")}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.ok}</p>
            <p className="text-sm text-green-600">{t("successful", "Successful")}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            <p className="text-sm text-red-600">{t("rejected", "Rejected")}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.warning}</p>
            <p className="text-sm text-amber-600">{t("warnings", "Warnings")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("gateId", "Gate ID")}</label>
              <Select
                value={filters.gateId}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, gateId: v }))}>
                
                <SelectTrigger>
                  <SelectValue placeholder="All Gates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allGates", "All Gates")}</SelectItem>
                  <SelectItem value="GATE_RCV_ITEM">{t("receivingItem", "Receiving Item")}</SelectItem>
                  <SelectItem value="GATE_PUT_BIN">{t("putawayBin", "Put-away Bin")}</SelectItem>
                  <SelectItem value="GATE_PICK_LOC">{t("pickLocation", "Pick Location")}</SelectItem>
                  <SelectItem value="GATE_PACK_ITEM">{t("packingItem", "Packing Item")}</SelectItem>
                  <SelectItem value="GATE_LOAD_PKG">{t("loadPackage", "Load Package")}</SelectItem>
                  <SelectItem value="GATE_STOP_PKG">{t("stopPackage", "Stop Package")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("barcode", "Barcode")}</label>
              <Input
                placeholder="Search barcode..."
                value={filters.barcodeValue}
                onChange={(e) => setFilters((prev) => ({ ...prev, barcodeValue: e.target.value }))} />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("result", "Result")}</label>
              <Select
                value={filters.result}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, result: v }))}>
                
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all", "All")}</SelectItem>
                  <SelectItem value="OK">{t("ok", "OK")}</SelectItem>
                  <SelectItem value="REJECTED">{t("rejected", "Rejected")}</SelectItem>
                  <SelectItem value="WARNING">{t("warning", "Warning")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />{t("search", "Search")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scan Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("time", "Time")}</TableHead>
                <TableHead>{t("gate", "Gate")}</TableHead>
                <TableHead>{t("barcode", "Barcode")}</TableHead>
                <TableHead>{t("type", "Type")}</TableHead>
                <TableHead>{t("result", "Result")}</TableHead>
                <TableHead>{t("operator", "Operator")}</TableHead>
                <TableHead>{t("reason", "Reason")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ?
              <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow> :
              scans.length === 0 ?
              <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{t("noScansFound", "No scans found")}

                </TableCell>
                </TableRow> :

              scans.map((scan) =>
              <TableRow
                key={scan.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setSelectedScan(scan)}>
                
                    <TableCell className="text-sm">{formatTime(scan.scanned_at)}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">{scan.gate_id}</code>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{scan.barcode_value}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{scan.barcode_type}</Badge>
                    </TableCell>
                    <TableCell>{getResultBadge(scan.scan_result)}</TableCell>
                    <TableCell className="text-sm">{scan.operator_id}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-48 truncate">
                      {scan.rejection_reason || '-'}
                    </TableCell>
                  </TableRow>
              )
              }
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Scan Detail Modal */}
      {selectedScan &&
      <TrailEventModal
        event={{
          ...selectedScan,
          event_type: `SCAN.${selectedScan.gate_id}`,
          timestamp_server: selectedScan.scanned_at,
          payload: {
            gate_id: selectedScan.gate_id,
            barcode_value: selectedScan.barcode_value,
            barcode_type: selectedScan.barcode_type,
            scan_result: selectedScan.scan_result,
            rejection_reason: selectedScan.rejection_reason,
            chain_mapping: selectedScan.chain_mapping
          }
        }}
        onClose={() => setSelectedScan(null)} />

      }
    </div>);

};

export default ScanTrail;